import { FC } from 'react';

import { Skeleton, Tooltip } from '@/components';
import { CHARTS_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind';

import { Card, CardContent } from '../../atoms/Card/Card';

type HeatmapData = {
  [x: string]: {
    [y: string]: {
      value: number;
    };
  };
};

type HeatMapChartProps = {
  title: string;
  data: {
    x: string;
    y: string;
    value: number;
  }[];
  className?: string;
  isEditCellSkeleton?: boolean;
};

const getColor = (value: number) => {
  if (value < 20) return '#FECDCA';
  else if (value < 40) return '#FFE5CB';
  else if (value < 60) return '#FFF3C0';
  else if (value < 80) return '#D4F8CF';
  else if (value <= 100) return '#B3E5D2';
};

export const HeatMapChart: FC<HeatMapChartProps> = ({
  title,
  data,
  className,
  isEditCellSkeleton,
}) => {
  const xLabels = Array.from(new Set(data.map(item => item.x)));
  const yLabels = Array.from(new Set(data.map(item => item.y)));

  const heatMapData: HeatmapData = data.reduce((acc, { x, y, value }) => {
    if (!acc[x]) acc[x] = {};
    acc[x][y] = { value };
    return acc;
  }, {} as HeatmapData);

  return (
    <Card
      data-testid={CHARTS_TEST_IDS.HEATMAP_CHART}
      className={cn('p-4 border-custom-warm-grey-150 rounded-[12px]', className)}
    >
      <CardContent className="flex flex-col">
        <div className="w-full mb-6">
          <h4 className="text-body-14-semibold !text-custom-warm-grey-700"> {title}</h4>
        </div>
        <div className="flex flex-col gap-3 items-start">
          {yLabels.map(yLabel => (
            <div key={yLabel} className="flex items-center gap-3 w-full">
              {isEditCellSkeleton ? (
                <Skeleton className="h-[12px] min-w-[140px]" />
              ) : (
                <p className="w-full text-body-12-regular text-custom-warm-grey-500 text-right truncate max-w-[240px]">
                  <Tooltip title={yLabel}>{yLabel}</Tooltip>
                </p>
              )}
              {xLabels.map(xLabel => {
                const { value } = heatMapData[xLabel]?.[yLabel] || {};
                return (
                  <div
                    key={xLabel}
                    className="w-full py-1.5 flex items-center justify-center rounded-[8px]"
                    style={{
                      backgroundColor: getColor(value),
                    }}
                  >
                    <span
                      className={cn('text-body-14-regular !text-custom-warm-grey-700', {
                        invisible: isEditCellSkeleton,
                      })}
                    >
                      {value ?? '-'}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}

          <div className="flex w-full">
            <div
              className={cn('max-w-[240px] w-full', {
                'min-w-[140px]': isEditCellSkeleton,
              })}
            />
            {xLabels.map(label => (
              <div key={label} className="w-full flex items-center justify-center text-center">
                {isEditCellSkeleton ? (
                  <Skeleton className="w-[60px] h-[12px] mt-1.5" />
                ) : (
                  <span className="text-body-12-regular text-custom-warm-grey-400">{label}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
